import { sentencePaletteForPack } from '../components/sentenceMap/palette.js';
// src/pages/PackReaderGrouped.jsx
//
// Shared student reader for "grouped section" pack types (literature-study,
// skills-workshop). Generalised VERBATIM from the literature reader (D3-B): the
// nav, multi-item sub-lists, practice mount (PracticeQuiz / PracticeEssayView /
// PracticeFlashcards), entitlement gating + locked CTA, chrome (ReaderPageShell +
// AI drawer + Notes drawer + StartHereCard) and inline audio are UNCHANGED.
//
// What each pack type supplies via `config`:
//   headerLabel      -- the small top tag ("Literature Study" / "Skills Workshop")
//   itemType         -- bookmark/notes item type (both 'lit_section')
//   enableFlashcards -- include a Flash Cards practice entry (skills: true; lit: false)
//   selectSections   -- (items) -> the content items for this reader
//   buildGroups      -- (sections) -> ordered nav groups [{ key, label, items }].
//                       Required ONLY when buildNavSections is absent (it is never
//                       called otherwise).
//   buildNavSections -- OPTIONAL (sections) -> ordered top-level nav sections
//                       [{ key, label, groups }] (groups = buildGroups shape). When
//                       present, the sidebar renders one labelled header per nav
//                       section (skills: Techniques / Past Papers) instead of the
//                       single "Sections" header; group keys must stay unique across
//                       ALL nav sections and groupKeyOf must match them. Absent ->
//                       today's single flat list via buildGroups (lit/poetry).
//   groupKeyOf       -- (item) -> the group key (for deep-link selection)
//   subListLabel     -- (item) -> label in a multi-item group's sub-list
//   renderSection    -- (item, group) -> the right-pane section render
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/useAuth.js';
import { retainArticleSession, findRunningIndependent, markArticleSessionSupported, articleSessionIndependent } from '../components/articlePilot/independentArticleSession.js';
import { resolveGroupedLocation, groupedReaderParams } from './groupedReaderLocation.js';
import { groupedFoundationQuizPool } from './groupedFoundationQuizzes.js';
import ArticlePilotRoute from '../components/articlePilot/ArticlePilotRoute.jsx';
import ArticleIntroductionLesson from '../components/articlePilot/ArticleIntroductionLesson.jsx';
import ArticleIntroductionQuiz from '../components/articlePilot/ArticleIntroductionQuiz.jsx';
import ArticleIntroductionSupplement from '../components/articlePilot/ArticleIntroductionSupplement.jsx';
import ArticleIntroductionSupplementQuiz from '../components/articlePilot/ArticleIntroductionSupplementQuiz.jsx';
import { SUPPLEMENT_QUIZ_KEY, supplementPartId, resolveArticleIntroductionSupplement, resolveSupplementQuizBundle, supplementQuizSourceIds } from '../components/articlePilot/articleIntroductionSupplement.js';
import { INTRO_QUIZ_ROUTE, INTRO_SKILL_PARTS, findArticleIntroductionLesson, introductionQuizBundle, introductionPartId } from '../components/articlePilot/articleIntroductions.js';
import CanonicalArticleStudy from '../components/articlePilot/CanonicalArticleStudy.jsx';
import { ARTICLE_PACK_ID, ARTICLE_ROUTE_ID, ARTICLE_MODEL_ID, ARTICLE_PARAGRAPH_GUIDE_ID, ARTICLE_PROMPT_IDS, ARTICLE_STEPS, findArticlePilotGuide, articlePilotStepContent } from '../components/articlePilot/articlePilotRoute.js';
import { useBetaContent } from '../analytics/useBetaContent.js';
import { doc, getDoc } from 'firebase/firestore';
import { db, AUTH_MODE } from '../auth/firebase.js';
import { usePackItems } from '../admin/useAdminPacks.js';
import { useEntitlements } from '../storage/useEntitlements.js';
import { useIsAdmin } from '../auth/useIsAdmin.js';
import { readerEntitled, adminBypassActive } from '../auth/adminView.js';
import { byDisplayOrder } from './readerGrouping.js';
import ReaderPageShell from './ReaderPageShell.jsx';
import ReaderAiDrawer from '../components/ReaderAiDrawer.jsx';
import ReaderNotesDrawer from '../components/ReaderNotesDrawer.jsx';
import ReaderItemActions from '../components/ReaderItemActions.jsx';
import JumpToAudioButton from '../components/JumpToAudioButton.jsx';
import { useBookmarks, userContentDocId } from '../storage/useUserContent.js';
import { IconCheck } from '../components/icons.jsx';
import { AudioList } from '../components/ReaderAudio.jsx';
import { AnnotationsContext, buildTermMatcher } from '../components/AnnotatedText.jsx';
import { selectPackAnnotations } from '../admin/packAnnotations.js';
import { selectAudioItems, selectQuizzes, selectEssayPrompts, selectFlashcards, partitionAudioByText } from './setTextReaderGate.js';
import SectionDrills from '../components/reader/SectionDrills.jsx';
import AskAiFab from '../components/reader/AskAiFab.jsx';
import { showAskAiFab } from '../components/reader/askAiFab.js';
import StartHereCard from '../components/StartHereCard.jsx';
import PracticeQuiz from '../components/practice/PracticeQuiz.jsx';
import PracticeEssayView from '../components/practice/PracticeEssayView.jsx';
import PracticeFlashcards from '../components/practice/PracticeFlashcards.jsx';
import { useProgress } from '../storage/useProgress.js';
import { ItemTickButton, SectionCompleteToggle } from '../components/reader/ProgressControls.jsx';
import LessonBottomBar from '../components/reader/LessonBottomBar.jsx';
import SectionsSheet from '../components/reader/SectionsSheet.jsx';
import { flattenLessonOrder, prevNextFor } from '../components/reader/lessonMeta.js';
import LessonToast from '../components/reader/LessonToast.jsx';
import NextLessonCard from '../components/reader/NextLessonCard.jsx';
import { nextContentEntry } from '../components/practice/feedbackState.js';
import { POOLED_QUIZ_KEY, progressPercent, groupProgress, unitsComplete } from '../storage/progressHelpers.js';
import { writeLastVisited, writePackStat } from '../storage/lastVisited.js';
import { mergeLessonSession } from '../components/lessonExtras/session.js';

export default function PackReaderGrouped({ config }) {
    const { packId } = useParams();
    const { currentUser } = useAuth();
    // Fresh hook instances prevent late old-owner/old-pack reads or roles from
    // joining a new reader. This resets only page memory, never saved drafts.
    const ownerUid = currentUser?.uid || null;
    return <GroupedReaderBody key={JSON.stringify([ownerUid, packId])} config={config} ownerUid={ownerUid} />;
}

function GroupedReaderBody({ config, ownerUid }) {
    const { packId } = useParams();

    // Pack header (public read of /packs/{packId}).
    const [pack, setPack] = useState(null);
    const [packLoading, setPackLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;
        if (AUTH_MODE === 'mock' || !db || !packId) { setPack(null); setPackLoading(false); return undefined; }
        setPackLoading(true);
        getDoc(doc(db, 'packs', packId))
            .then((snap) => { if (!cancelled) { setPack(snap.exists() ? { id: snap.id, ...snap.data() } : null); setPackLoading(false); } })
            .catch((e) => { if (!cancelled) { console.error('PackReaderGrouped pack:', e); setPack(null); setPackLoading(false); } });
        return () => { cancelled = true; };
    }, [packId]);

    // Optional Tutor Review fee note (L3) -- public/authed read of the admin config
    // doc; absent/denied -> null -> the panel shows a neutral fallback. Best-effort.
    const [tutorFeeNote, setTutorFeeNote] = useState(null);
    useEffect(() => {
        let cancelled = false;
        if (AUTH_MODE === 'mock' || !db) return undefined;
        getDoc(doc(db, 'config', 'tutor-review-settings'))
            .then((snap) => {
                const fee = snap.exists() && snap.data().content && snap.data().content.feeDescription;
                if (!cancelled) setTutorFeeNote(typeof fee === 'string' && fee.trim() ? fee.trim() : null);
            })
            .catch(() => { /* not configured / denied -> fallback copy */ });
        return () => { cancelled = true; };
    }, []);

    // Slice 3 -- pack glossary/annotations: public read of /config/terminology (the SAME
    // doc the Terminology page uses). Best-effort; absent/denied -> no glossary. Build the
    // matcher ONCE (this pack's annotations first, then global terms) for the in-reader
    // popovers; AnnotatedText (consumed by the section renderers) reads it via context.
    const [glossaryTerms, setGlossaryTerms] = useState([]);
    useEffect(() => {
        let cancelled = false;
        if (AUTH_MODE === 'mock' || !db) return undefined;
        getDoc(doc(db, 'config', 'terminology'))
            .then((snap) => {
                const c = snap.exists() ? snap.data().content : null;
                const terms = (c && Array.isArray(c.terms)) ? c.terms : [];
                if (!cancelled) setGlossaryTerms(terms);
            })
            .catch(() => { /* not configured / denied -> no glossary */ });
        return () => { cancelled = true; };
    }, []);
    const termMatcher = useMemo(() => {
        const packAnns = selectPackAnnotations(glossaryTerms, packId);
        const globals = (glossaryTerms || []).filter((t) => t && t.scope !== 'pack');
        return buildTermMatcher([...packAnns, ...globals]);
    }, [glossaryTerms, packId]);

    // Free sections always; paid sections STRICT-GATED on entitlement (same as set-text).
    const { items: freeItems, loading: freeLoading, error: freeError } = usePackItems(packId, 'freeItems');
    const { activePackIds, loading: entLoading } = useEntitlements();
    // Admin read-view bypass: an admin may LOAD paid content without an entitlement
    // (firestore.rules already permits admin paidItems reads). Client VIEW only --
    // no entitlement record / purchase logic changes; tutors do NOT bypass. adminView.js.
    const realEntitlement = !entLoading && activePackIds.has(packId);
    const isAdmin = useIsAdmin();
    const entitled = readerEntitled({ realEntitlement, isAdmin });
    const { items: loadedPaidItems, loading: paidLoading, settled: paidSettled, error: paidError } = usePackItems(packId, 'paidItems', entitled);
    // Exclude old arrays synchronously on access/pack changes, before hook cleanup.
    const ownPackLoaded = pack?.id === packId;
    const paidItems = useMemo(() => entitled && ownPackLoaded && paidSettled && !paidLoading ? loadedPaidItems : [], [entitled, ownPackLoaded, paidSettled, paidLoading, loadedPaidItems]);
    const visibleFreeItems = useMemo(() => ownPackLoaded && !freeLoading ? freeItems : [], [ownPackLoaded, freeLoading, freeItems]);

    const freeSections = useMemo(() => config.selectSections(visibleFreeItems), [config, visibleFreeItems]);
    const paidSections = useMemo(() => config.selectSections(paidItems), [config, paidItems]);
    // Combined cross-scope sequence (section-ordering S1): free + paid share one
    // displayOrder-driven order. Sorts only what is ALREADY loaded -- the paid
    // array above remains strictly entitlement-gated; gating is untouched.
    const allSections = useMemo(() => byDisplayOrder([...freeSections, ...paidSections]), [freeSections, paidSections]);

    // Inline audio: free audio from freeItems (always), paid audio from paidItems (only
    // loaded when entitled). Each audio EITHER links to a section via content.relatedTextId
    // (rendered inline under that section) OR is pack-level (no/unknown relatedTextId ->
    // rendered in the pack-level "Analysis Podcasts" block so it's never invisible).
    const freeAudio = useMemo(() => selectAudioItems(visibleFreeItems).map((a) => ({ ...a, _paid: false })), [visibleFreeItems]);
    const paidAudio = useMemo(() => selectAudioItems(paidItems).map((a) => ({ ...a, _paid: true })), [paidItems]);
    const visibleAudio = useMemo(() => [...freeAudio, ...paidAudio], [freeAudio, paidAudio]);
    const knownItemIds = useMemo(() => new Set(allSections.map((s) => s.id)), [allSections]);
    const { byText: audioByItemId, packLevel: packAudio } = useMemo(
        () => partitionAudioByText(visibleAudio, knownItemIds),
        [visibleAudio, knownItemIds],
    );

    // Optional two-tier nav (config.buildNavSections): ordered labelled nav-sections,
    // each holding buildGroups-shaped groups. navGroups stays the FLAT concat of every
    // nav-section's groups, so selection (selKey/activeGroup/selItem), the deep-link
    // and progress all run off the same flat list as before -- only the sidebar's
    // visual grouping changes. Without the hook: today's single list via buildGroups.
    const navSections = useMemo(
        () => (config.buildNavSections ? config.buildNavSections(allSections) : null),
        [config, allSections],
    );
    const navGroups = useMemo(
        () => (navSections ? navSections.flatMap((ns) => ns.groups) : config.buildGroups(allSections)),
        [navSections, config, allSections],
    );

    // Practice section: the pack's quiz items (pooled), optional flashcards, and each
    // essay_prompt by title. Same gating -- free always, paid only when entitled.
    const quizItems = useMemo(
        () => [...selectQuizzes(visibleFreeItems), ...selectQuizzes(paidItems)],
        [visibleFreeItems, paidItems],
    );
    const flashcardItems = useMemo(
        () => (config.enableFlashcards ? [...selectFlashcards(visibleFreeItems), ...selectFlashcards(paidItems)] : []),
        [config, visibleFreeItems, paidItems],
    );
    const essayItems = useMemo(
        () => [...selectEssayPrompts(visibleFreeItems), ...selectEssayPrompts(paidItems)],
        [visibleFreeItems, paidItems],
    );
    const introductionLesson = packId === ARTICLE_PACK_ID ? findArticleIntroductionLesson({ entitled, paidItems }) : null;
    const introductionBundle = introductionQuizBundle({ entitled, lesson: introductionLesson, items: quizItems });
    const supplementLesson = useMemo(() => packId === ARTICLE_PACK_ID ? resolveArticleIntroductionSupplement({ entitled, items: paidItems }) : null, [packId, entitled, paidItems]);
    const supplementBundle = useMemo(() => resolveSupplementQuizBundle({ entitled, items: quizItems, lesson: supplementLesson }), [entitled, quizItems, supplementLesson]);
    const supplementSources = useMemo(() => packId === ARTICLE_PACK_ID ? supplementQuizSourceIds({ entitled, items: paidItems }) : new Set(), [packId, entitled, paidItems]);
    // Dedicated foundation banks keep one practice/progress path, including when
    // their labelled source bank is partial or unavailable. No URL grants access.
    const pooledQuizItems = groupedFoundationQuizPool(quizItems, introductionLesson?.id, supplementSources);
    const practiceNav = useMemo(() => {
        const entries = [];
        if (packId === ARTICLE_PACK_ID) entries.push({ id: ARTICLE_ROUTE_ID, kind: 'articlePilot', label: 'Article: Learn, Practise, Review' });
        if (introductionBundle.items.length) entries.push({ id: INTRO_QUIZ_ROUTE, kind: 'introductionQuiz', label: 'Article Introduction foundation practice' });
        if (supplementBundle.items.length) entries.push({ id: SUPPLEMENT_QUIZ_KEY, kind: 'introductionSupplementQuiz', label: 'Introduction supplement foundation practice' });
        if (pooledQuizItems.length > 0) entries.push({ id: '__quiz__', kind: 'quiz', label: 'Quiz' });
        if (config.enableFlashcards && flashcardItems.length > 0) entries.push({ id: '__flashcards__', kind: 'flashcards', label: 'Flash Cards' });
        for (const it of essayItems) {
            entries.push({ id: it.id, kind: 'essay', label: it.title || 'Timed Essay', item: it });
        }
        return entries;
    }, [config, packId, pooledQuizItems.length, flashcardItems, essayItems, introductionBundle.items.length, supplementBundle.items.length]);

    // The URL is the selection pointer for sections AND actual practice types.
    // It never grants access or starts a timed attempt.
    const [searchParams, setSearchParams] = useSearchParams();
    const wantItem = searchParams.get('item');
    const locationLoading = packLoading || !ownPackLoaded || freeLoading || entLoading || (entitled && (!paidSettled || paidLoading));
    const locationFailed = !!freeError || (entitled && !!paidError);
    const selectedLocation = resolveGroupedLocation({ sections: allSections, practiceNav, wantedId: wantItem, loading: locationLoading, failed: locationFailed });
    const activePractice = selectedLocation.status === 'ready' ? selectedLocation.practice : null;
    const selItem = selectedLocation.status === 'ready' ? selectedLocation.section : null;
    const activeGroup = selItem ? navGroups.find(g => g.key === config.groupKeyOf(selItem)) || null : null;
    const activeItems = activeGroup ? activeGroup.items : [];
    const isArticlePack = packId === ARTICLE_PACK_ID;
    const requestedStep = searchParams.get('articleStep');
    const articleStepId = ARTICLE_STEPS.some(step => step.id === requestedStep) ? requestedStep : ARTICLE_STEPS[0].id;
    const articleGuide = findArticlePilotGuide({ entitled, paidItems });
    const introductionSelected = introductionLesson && selItem?.id === introductionLesson.id;
    const introPart = introductionPartId(searchParams.get('introPart'));
    const supplementSelected = supplementLesson && selItem?.id === supplementLesson.id;
    const supplementPart = supplementPartId(searchParams.get('suppPart'));
    const studyView = activePractice?.id === ARTICLE_MODEL_ID && ['study', 'study-opening'].includes(searchParams.get('view'));
    const requestedIndependent = activePractice?.kind === 'essay' && searchParams.get('view') === 'independent'
        && [ARTICLE_PROMPT_IDS.partTime, ARTICLE_PROMPT_IDS.neighbourhood].includes(activePractice.id);


    // Persisted progress (Scope A): manual completion ticks + quiz runs.
    const { completedSet, quizScores, markItemComplete, unmarkItemComplete, recordQuizRun, quizState, saveQuizState, enabled: progressEnabled } = useProgress(packId);
    // Manual progress (reader-manual-progress): opening a section no longer
    // auto-completes it. The user ticks items via the sidebar checkbox or the
    // section "Mark complete" toggle -- both call this.
    // lesson-3b: the completion moment. One toast at a time (a second mark-complete
    // replaces the first rather than stacking); nothing is persisted.
    const [toast, setToast] = useState(null);
    const toggleItemComplete = useCallback(
        (itemId) => { if (completedSet.has(itemId)) unmarkItemComplete(itemId); else markItemComplete(itemId); },
        [completedSet, markItemComplete, unmarkItemComplete]
    );

    const [aiOpen, setAiOpen] = useState(false);
    const [notesOpen, setNotesOpen] = useState(false);
    // The ONE open-the-drawer handler: the header pill and the floating button
    // (reader-ai-fab) both call it. Passing it to the shell is also the header's
    // render guard, so the FAB reads the same value to decide whether AI is offered.
    const openAi = useCallback(() => setAiOpen(true), []);
    // lesson-3a: below lg the nav lives in a bottom SHEET (opened from the lesson bar),
    // not the old inline disclosure dropdown. Selecting anything closes it.
    const [sheetOpen, setSheetOpen] = useState(false);
    const closeNav = useCallback(() => setSheetOpen(false), []);

    // N1-b: a deep-link from the Account "My Notes" list (?item=<id>&notes=1) auto-opens
    // the notes drawer once that section is actually selected, so the drawer targets it.
    const notesAutoOpened = useRef(null);
    useEffect(() => {
        const key = packId + '/' + wantItem;
        if (notesAutoOpened.current === key || searchParams.get('notes') !== '1') return;
        if (selItem && (!wantItem || selItem.id === wantItem)) {
            setNotesOpen(true);
            notesAutoOpened.current = key;
        }
    }, [packId, searchParams, wantItem, selItem]);

    const navigateItem = (id, options = {}) => {
        setSearchParams(groupedReaderParams(searchParams, id, options));
        setAiOpen(false); setNotesOpen(false); closeNav();
        window.scrollTo({ top: 0, behavior: 'auto' });
    };
    const returnToArticle = (id = articleStepId) => navigateItem(ARTICLE_ROUTE_ID, { articleStep: id });
    const openArticleStudy = ({ mode = 'full', stepId = articleStepId } = {}) => navigateItem(ARTICLE_MODEL_ID, { view: mode === 'opening' ? 'study-opening' : 'study', articleStep: stepId });
    const openIntroduction = (part = 'intro_overview') => {
        if (introductionLesson) navigateItem(introductionLesson.id, { articleStep: 'article_pilot_2', introPart: introductionPartId(part) });
    };
    const openIntroductionQuiz = () => {
        if (introductionBundle.items.length) navigateItem(INTRO_QUIZ_ROUTE, { articleStep: 'article_pilot_2' });
    };
    const openSupplement = (part = 'supp_entrance_question') => {
        if (!supplementLesson) return;
        const selected = supplementPartId(part);
        updateArticleDraft('supplementNavigation', { part: selected });
        navigateItem(supplementLesson.id, { articleStep: 'article_pilot_2', suppPart: selected });
    };
    const openSupplementQuiz = () => {
        if (!supplementBundle.items.length) return;
        if (supplementSelected) updateArticleDraft('supplementNavigation', { part: supplementPart });
        navigateItem(SUPPLEMENT_QUIZ_KEY, { articleStep: 'article_pilot_2' });
    };
    const beginIndependentArticle = id => {
        if ([ARTICLE_PROMPT_IDS.partTime, ARTICLE_PROMPT_IDS.neighbourhood].includes(id)) navigateItem(id, { view: 'independent', articleStep: 'article_pilot_7' });
    };

    // Bookmarks (user-owned). The active item's denormalized title for save.
    const { bookmarkIds, toggleBookmark, uid: bmUid } = useBookmarks();
    const activeItemTitle = (selItem && selItem.content && selItem.content.title) || (activeGroup && activeGroup.label) || null;
    // Page memory only. Keep all existing acknowledged My Essays snapshots intact.
    const ownerKey = packId + '/' + (ownerUid || 'signed-out');
    const essaySessions = useRef({ owner: ownerKey, values: new Map() });
    if (essaySessions.current.owner !== ownerKey) essaySessions.current = { owner: ownerKey, values: new Map() };
    const [missionSessions, setMissionSessions] = useState({});
    const [articleDraftState, setArticleDraftState] = useState({ owner: ownerKey, values: {} });
    const articleDrafts = articleDraftState.owner === ownerKey ? articleDraftState.values : {};
    const updateArticleDraft = (id, patch) => setArticleDraftState(previous => ({ owner: ownerKey, values: { ...(previous.owner === ownerKey ? previous.values : {}), [id]: { ...(previous.owner === ownerKey ? previous.values[id] : {}), ...patch } } }));
    const [, refreshEssaySessions] = useState(0);
    const essaySession = activePractice?.kind === 'essay' ? essaySessions.current.values.get(activePractice.id) : null;
    const runningIndependent = findRunningIndependent(essaySessions.current.values);
    const independentView = articleSessionIndependent(essaySession, requestedIndependent);
    const independentWriting = !!runningIndependent;
    const independentGuard = independentWriting && (activePractice?.id !== runningIndependent.promptId || studyView);
    const retainEssaySession = snapshot => {
        if (essaySessions.current.owner !== ownerKey) return;
        const previous = essaySessions.current.values.get(snapshot.promptId);
        const next = retainArticleSession(previous, snapshot, { requestedIndependent });
        essaySessions.current.values.set(snapshot.promptId, next);
        // Text/ticks stay in refs; re-render only when the mode or phase changes.
        if (!previous || previous.phase !== next.phase || previous.independent !== next.independent || previous.supportUsed !== next.supportUsed) refreshEssaySessions(value => value + 1);
    };
    const receiveEssayPhase = phase => {
        if (phase === 'running' && independentView) { setAiOpen(false); setNotesOpen(false); setSheetOpen(false); }
    };
    useBetaContent(packId, selItem?.id, 'section', !!selItem && selectedLocation.remember && !independentGuard);
    const switchToSupported = () => {
        if (!runningIndependent) return;
        essaySessions.current.values.set(runningIndependent.promptId, markArticleSessionSupported(runningIndependent));
        updateArticleDraft('article_pilot_7', { supportUsed: 'Switched to supported practice during this timed attempt.' });
        refreshEssaySessions(value => value + 1);
    };

    // "Continue where you left off" (library-2-shelf): remember the section on screen,
    // plus this pack's completion -- the reader is the only place that knows the item
    // total, and the library must not add a Firestore read to find it. localStorage
    // only; a blocked/full store just leaves the Continue card hidden.
    // Placed ABOVE the mock-mode early return below: a hook after a conditional return
    // would break the rules of hooks the first time that branch flips.
    useEffect(() => {
        if (!packId || !selItem || !selectedLocation.remember || independentGuard) return;
        const total = allSections.length;
        const done = allSections.reduce((n, s) => n + (completedSet.has(s.id) ? 1 : 0), 0);
        const ts = Date.now();
        writeLastVisited({
            packId,
            itemId: selItem.id,
            title: activeItemTitle || '',
            packTitle: (pack && pack.metadata && pack.metadata.title) || packId || '',
            ts, done, total,
        });
        writePackStat(packId, { done, total, ts });
    }, [packId, selItem, activeItemTitle, pack, allSections, completedSet, selectedLocation.remember, independentGuard]);
    // Label for the mobile section-nav dropdown (current practice / item / group).
    const activeBookmarkId = selItem ? userContentDocId(packId, selItem.id) : null;

    // Jump-to-audio targets: the active section's inline audio (textAudioRef) and the
    // pack-level "Analysis Podcasts" block (packAudioRef). The button shows whenever EITHER
    // has audio (hasAnyAudio) -- so a pack whose audio is all section-linked still gets a
    // jump affordance -- and scrolls to the section's audio first, else the pack-level block.
    // Section audio only counts when NOT in a practice view (where the inline block isn't
    // rendered); there the button falls back to the pack-level block.
    const packAudioRef = useRef(null);
    const textAudioRef = useRef(null);
    const hasTextAudio = !activePractice && !!selItem && (audioByItemId[selItem.id] || []).length > 0;
    const hasAnyAudio = !independentWriting && (hasTextAudio || packAudio.length > 0);
    const jumpToAudio = () => {
        const target = (hasTextAudio && textAudioRef.current) ? textAudioRef.current : packAudioRef.current;
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    if (AUTH_MODE === 'mock' || !db) {
        return (
            <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-book-bg paper-texture">
                <div className="max-w-3xl mx-auto px-4 md:px-8 py-12">
                    <p className="font-serif italic text-book-muted">
                        The reader needs Firestore (emulator or firebase mode). It is unavailable in mock mode.
                    </p>
                </div>
            </div>
        );
    }

    const title = (pack && pack.metadata && pack.metadata.title) || packId;
    const subtitle = pack && pack.metadata && pack.metadata.subtitle;
    const initialLoading = (packLoading || freeLoading) && allSections.length === 0;
    const showLockedCta = !entLoading && !entitled; // non-buyer paywall hint (paid sections not loaded)
    const showAdminView = adminBypassActive({ realEntitlement, isAdmin }); // admin viewing paid without an entitlement
    // Progress (Scope A): completed sections / visible sections + a per-unit
    // rollup (how many nav groups are fully complete) and the pooled quiz score,
    // if taken. Completion-based (opened = done), not a mastery/performance grade.
    const doneCount = allSections.reduce((n, s) => n + (completedSet.has(s.id) ? 1 : 0), 0);
    const progressPct = progressPercent(doneCount, allSections.length);
    const unitsDone = unitsComplete(navGroups, completedSet);
    const pooledQuiz = quizScores && quizScores[POOLED_QUIZ_KEY];

    const quizRollup = pooledQuiz && Number(pooledQuiz.total) > 0 ? pooledQuiz : null;

    // One nav group row (+ its item sub-list when active). Shared verbatim by both
    // sidebar shapes: the single flat "Sections" list and the two-tier nav-sections.
    const renderNavGroup = (s) => {
        const active = activeGroup && activeGroup.key === s.key;
        const count = s.items.length;
        const gp = groupProgress(s, completedSet);
        return (
            <div key={s.key}>
                <button onClick={() => navigateItem(s.items[0].id)}
                    className={`w-full text-left px-3 py-2 mb-1 border-l-2 transition-colors ${active ? 'bg-book-accent/10 border-book-accent' : 'border-transparent hover:bg-book-accent/5'}`}>
                    <div className="flex items-center justify-between gap-2">
                        <span className="flex items-center gap-2 min-w-0">
                            {/* Single-item group == one section -> a left tick toggle for it.
                                Multi-item groups are containers: their items are ticked in the
                                sub-list below; the row keeps the aggregate rollup on the right. */}
                            {count === 1 && progressEnabled && (
                                <ItemTickButton complete={completedSet.has(s.items[0].id)}
                                    onToggle={() => toggleItemComplete(s.items[0].id)} label={s.label} />
                            )}
                            <span className={`font-serif text-sm leading-tight ${active ? 'text-book-text' : 'text-book-muted'}`}>{s.label}</span>
                        </span>
                        <span className="flex items-center gap-1.5 flex-shrink-0">
                            {count > 1 && gp.total > 0 && <span className="font-display text-xs text-book-accent/70 font-semibold">{gp.done}/{gp.total}</span>}
                            {count > 1 && gp.complete && <IconCheck className="w-3 h-3 text-book-accent" />}
                        </span>
                    </div>
                    {/* Per-unit completion bar (opened = done; not a mastery score). */}
                    {gp.total > 0 && (
                        <div className="mt-1.5 h-1 bg-book-accent/10 rounded-full overflow-hidden">
                            <div className="h-full bg-book-accent/60 transition-all duration-500" style={{ width: gp.percent + '%' }} />
                        </div>
                    )}
                </button>
                {/* item sub-list for the active multi-item group */}
                {active && count > 1 && (
                    <div className="ml-3 mb-1">
                        {activeItems.map((it) => {
                            const itActive = selItem && selItem.id === it.id;
                            return (
                                <button key={it.id} onClick={() => navigateItem(it.id)}
                                    className={`w-full text-left px-3 py-1.5 flex items-center gap-2 border-l-2 transition-colors ${itActive ? 'border-book-accent text-book-text' : 'border-transparent text-book-muted hover:text-book-text'}`}>
                                    {progressEnabled && (
                                        <ItemTickButton complete={completedSet.has(it.id)}
                                            onToggle={() => toggleItemComplete(it.id)} label={config.subListLabel(it)} />
                                    )}
                                    <span className="font-serif text-xs leading-tight truncate">{config.subListLabel(it)}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    // Admin read-view indicator (adminView.js). lesson-3a: a thin line under the top
    // bar rather than a band in the content column. Admin-only, unchanged in meaning.
    const adminBanner = showAdminView ? (
        <div className="border-b border-book-accent/30 bg-book-accent/5">
            <div className="max-w-6xl mx-auto px-4 md:px-8 py-1.5 flex flex-wrap items-center gap-x-2">
                <span className="font-display text-book-accent text-xs font-semibold">Admin view</span>
                <span className="font-serif italic text-book-muted text-xs">Paid content shown without an entitlement.</span>
            </div>
        </div>
    ) : null;

    // lesson-3a: Prev/Next walk the nav in reading order, crossing group boundaries --
    // the same order the sidebar shows, so "Next" never surprises.
    const lessonOrder = flattenLessonOrder(navGroups, practiceNav);
    const currentEntryId = activePractice ? activePractice.id : (selItem ? selItem.id : null);
    const { prev: prevEntry, next: nextEntry } = prevNextFor(lessonOrder, currentEntryId);
    const goEntry = (entry) => {
        if (!entry) return;
        navigateItem(entry.id);
    };

    // lesson-3b: the next LESSON (practice entries skipped), for the end-of-section card
    // and the toast's action. Null at the end of the unit.
    const nextLesson = selItem ? nextContentEntry(lessonOrder, selItem.id) : null;
    // Blocks of a section by id, so NextLessonCard can show its reading time without
    // the reader having to hand it the whole item.
    const blocksFor = (id) => {
        const it = allSections.find((s) => s.id === id);
        return (it && it.content && it.content.blocks) || null;
    };

    // The Mark-complete controls (desktop toggle + the mobile lesson bar) raise a toast
    // when they mark something DONE. Un-marking is silent -- a toast celebrating an
    // undo would be noise. The sidebar ticks deliberately do NOT toast: ticking a row
    // for some other section is bookkeeping, not a completion moment.
    const markCompleteWithToast = (itemId) => {
        const wasComplete = completedSet.has(itemId);
        toggleItemComplete(itemId);
        if (wasComplete) { setToast(null); return; }
        setToast({
            id: itemId + ':' + Date.now(),
            message: nextLesson ? 'Section done · Next: ' + nextLesson.title : 'Section done',
            actionLabel: nextLesson ? 'Next' : null,
            next: nextLesson,
        });
    };

    // lesson-3a: the nav is built ONCE here and rendered twice -- in the desktop
    // sidebar and, below lg, inside the sections sheet. Same groups, same ticks, same
    // progress bar, same practice entries: the mobile nav cannot drift from the desktop
    // one because there is only one of it.
    const navContent = (
        <>
        {/* lesson-3a: "Start here" is a chip in the nav now, not a band above the
            lesson -- the steps are worth reading once, not on every visit. */}
        <div className="px-1 pt-1">
            {!independentWriting && <StartHereCard packId={packId} startHere={pack && pack.startHere}
                routeAction={isArticlePack ? { label: 'Open the Article route', onOpen: () => returnToArticle(ARTICLE_STEPS[0].id) } : null} />}
            {isArticlePack && <button type="button" onClick={() => returnToArticle()} className="w-full text-left px-3 py-2 text-book-accent border border-book-accent/30 rounded-full text-sm">Article · Learn / Practise / Review</button>}
        </div>
        {allSections.length > 0 && (
            <div className="px-3 pt-2 pb-1">
                <div className="flex items-center justify-between mb-1">
                    <span className="font-display text-book-muted text-xs font-semibold">Progress</span>
                    <span className="font-display text-book-accent text-xs font-semibold">{doneCount} / {allSections.length}</span>
                </div>
                <div className="h-1.5 bg-book-accent/10 rounded-full overflow-hidden">
                    <div className="h-full bg-book-accent transition-all duration-500" style={{ width: progressPct + '%' }} />
                </div>
                {(unitsDone.total > 0 || quizRollup) && (
                    <div className="flex items-center gap-2 mt-1.5">
                        {unitsDone.total > 0 && <span className="font-serif italic text-book-muted text-xs font-semibold">{unitsDone.complete} of {unitsDone.total} units complete</span>}
                        {quizRollup && <span className="font-display text-book-accent/80 text-xs ml-auto font-semibold">Quiz {quizRollup.score} correct · {quizRollup.answered ?? quizRollup.total}/{quizRollup.total} answered</span>}
                    </div>
                )}
            </div>
        )}
        {navSections ? (
            /* Two-tier nav (config.buildNavSections): one labelled header per
               content-type section (skills: Techniques / Past Papers), each
               holding the SAME group rows as the flat list. Later sections get
               the Practice block's divider treatment. */
            navSections.map((ns, i) => (
                <div key={ns.key} className={i > 0 ? 'mt-3 pt-2 border-t border-book-accent/15' : undefined}>
                    <div className="font-display text-book-accent text-sm px-3 py-2 font-medium rounded-full">{ns.label}</div>
                    {ns.groups.map(renderNavGroup)}
                </div>
            ))
        ) : (
            <>
                <div className="font-display text-book-accent text-sm px-3 py-2 font-medium rounded-full">Sections</div>
                {navGroups.map(renderNavGroup)}
            </>
        )}

        {/* Practice group: quiz + (skills) flashcards + essay activities, by title. */}
        {practiceNav.length > 0 && (
            <div className="mt-3 pt-2 border-t border-book-accent/15">
                <div className="font-display text-book-accent text-sm px-3 py-2 font-medium rounded-full">Practice</div>
                {practiceNav.map((p) => {
                    const active = activePractice && activePractice.id === p.id;
                    return (
                        <button key={p.id} onClick={() => navigateItem(p.id)}
                            className={`w-full text-left px-3 py-2 mb-1 flex items-center gap-2 border-l-2 transition-colors ${active ? 'bg-book-accent/10 border-book-accent' : 'border-transparent hover:bg-book-accent/5'}`}>
                            <span className={`font-serif text-sm leading-tight truncate ${active ? 'text-book-text' : 'text-book-muted'}`}>{p.label}</span>
                        </button>
                    );
                })}
            </div>
        )}

        {showLockedCta && (
            <div className="mt-3 mx-2 border border-book-accent/20 bg-book-bg/40 p-3 text-center">
                <div className="font-display text-book-muted/80 text-xs mb-1 font-semibold">🔒 More with purchase</div>
                <p className="font-serif italic text-book-muted/70 text-xs leading-snug font-semibold">
                    The full set of sections and analysis unlocks with the pack.
                </p>
            </div>
        )}
        </>
    );
    return (
        <AnnotationsContext.Provider value={termMatcher}>
        <ReaderPageShell packTitle={title} onAskAi={independentWriting ? null : openAi} adminBanner={adminBanner}>
            {/* lesson-3a: the pack header band is gone -- the title lives in the compact
                top bar, and each section opens with its own lesson header card.
                btl-lesson-scrollpad reserves room below lg for the fixed lesson bar. */}
            <div data-sentence-palette={sentencePaletteForPack(packId)} className="max-w-6xl mx-auto px-4 md:px-8 py-5 md:py-10 btl-lesson-scrollpad">
                {subtitle && (
                    <p className="font-serif italic text-book-muted text-sm mb-4 lg:hidden">{subtitle}</p>
                )}

                {initialLoading ? (
                    <p className="font-serif italic text-book-muted">Loading…</p>
                ) : navGroups.length === 0 && practiceNav.length === 0 ? (
                    <div className="border border-book-accent/20 bg-book-page/30 p-10 text-center">
                        <p className="font-serif italic text-book-muted">No content available in this pack yet.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6">
                        {/* LEFT: section nav (+ item list for the active multi-item group) + locked CTA.
                            lesson-3a: hidden below lg -- the SAME `navContent` renders inside the
                            bottom sheet there, so there is one nav, not two. */}
                        <aside className="hidden lg:block rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-2 h-fit lg:sticky lg:top-16 max-h-[78vh] overflow-y-auto custom-scrollbar">
                            {navContent}
                        </aside>

                        {/* RIGHT: per-item actions (bookmark + Notes -- chrome, OUTSIDE
                            .analysis-content so they stay clickable) + the analysis render.
                            .analysis-content (ADR-009) blocks copy on the analysis only.
                            slicemx1: min-w-0 lets this 1fr grid track shrink below its content's
                            min-content (default minmax(auto,1fr) floor) -- without it, wide content
                            like the Theme x Character matrix blows the column past the viewport and
                            its overflow-x:auto scroll container can't reach the last columns. */}
                        <div className="min-w-0">
                            {bmUid && selItem && !independentWriting && (
                                <ReaderItemActions
                                    isBookmarked={bookmarkIds.has(activeBookmarkId)}
                                    onToggleBookmark={() => toggleBookmark({ packId, itemId: selItem.id, itemType: config.itemType, title: activeItemTitle })}
                                    onOpenNotes={() => setNotesOpen(true)} />
                            )}
            {!initialLoading && lessonOrder.length > 0 && !activePractice && !independentWriting && !aiOpen && !notesOpen && (
                <LessonBottomBar
                    onPrev={prevEntry ? () => goEntry(prevEntry) : null}
                    onNext={nextEntry ? () => goEntry(nextEntry) : null}
                    prevLabel={prevEntry && prevEntry.title}
                    nextLabel={nextEntry && nextEntry.title}
                    complete={!!(selItem && completedSet.has(selItem.id))}
                    showComplete={!!(progressEnabled && selItem)}
                    onToggleComplete={() => selItem && markCompleteWithToast(selItem.id)}
                    onOpenSections={() => setSheetOpen(true)} />
            )}
                            {/* Manual "Mark complete" toggle for the current section -- in sync
                                with the sidebar tick (same completedSet). Practice activities
                                (selItem null) have their own completion, so it only shows here.
                                lesson-3a: hidden below lg, where the lesson bar carries it. */}
                            {progressEnabled && selItem && !independentWriting && (
                                <div className="mb-4 hidden lg:block">
                                    <SectionCompleteToggle complete={completedSet.has(selItem.id)}
                                        onToggle={() => markCompleteWithToast(selItem.id)} />
                                </div>
                            )}
                            {/* Below the Save/Notes group: jump to audio reachable from this
                                view -- the active section's inline audio or the pack-level
                                podcasts. Auto-hidden when the view has no audio at all. */}
                            {hasAnyAudio && <JumpToAudioButton onJump={jumpToAudio} />}
                            {independentGuard ? <section className="rounded-[var(--radius-card)] border border-book-line bg-book-page p-5" aria-labelledby="independent-guard-title">
                                <h2 id="independent-guard-title" className="font-display text-book-accent">Your independent article is still running</h2>
                                <p className="my-3">Continue writing, or switch this attempt to supported practice to open lessons, models and hints. Your text and original timer stay on this page.</p>
                                <div className="flex flex-wrap gap-3">
                                    <button type="button" onClick={() => beginIndependentArticle(runningIndependent.promptId)} className="px-4 py-2 rounded-full bg-book-accent text-white">Continue independent writing</button>
                                    <button type="button" onClick={switchToSupported} className="px-4 py-2 rounded-full border border-book-line">Switch to supported practice</button>
                                </div>
                            </section> : selectedLocation.status === 'pending' ? <p role="status" className="font-serif text-book-muted">Loading the requested content…</p>
                            : selectedLocation.status === 'unavailable' ? <div role="status" className="rounded-[var(--radius-card)] border border-book-line bg-book-page p-5"><h2 className="font-display text-book-accent">This content is unavailable</h2><p>It may no longer be available, or your account may need access. Choose an available section from the menu.</p><button type="button" onClick={() => setSheetOpen(true)} className="mt-3 underline text-book-accent">Open Sections</button></div>
                            : activePractice ? (
                                /* Practice activities render OUTSIDE .analysis-content (interactive;
                                   the essay textarea must stay selectable/typable). */
                                <div>
                                <div className="mb-4 flex flex-wrap items-center gap-3 text-sm">
                                    <button type="button" onClick={() => setSheetOpen(true)} className="text-book-accent underline">Sections</button>
                                    {isArticlePack && !independentWriting && <button type="button" onClick={() => returnToArticle()} className="text-book-accent underline">Back to Article route</button>}
                                    {activePractice.id === ARTICLE_MODEL_ID && !studyView && !independentWriting && <button type="button" onClick={() => openArticleStudy()} className="text-book-accent underline">Study the original model</button>}
                                    {independentView && <span className="text-book-muted">Independent practice · one article · 45 minutes</span>}
                                </div>
                                {activePractice.kind === 'articlePilot' ? <ArticlePilotRoute guide={articleGuide} entitled={entitled} currentStepId={articleStepId}
                                    onStep={returnToArticle} onNavigate={id => navigateItem(id, { articleStep: articleStepId })} onStudy={openArticleStudy} onBeginIndependent={beginIndependentArticle}
                                    drafts={articleDrafts} onDraftChange={updateArticleDraft} introductionAvailable={!!introductionLesson} onIntroduction={() => openIntroduction()}
                                    supplementAvailable={!!supplementLesson} onSupplement={() => openSupplement()} />
                                : activePractice.kind === 'introductionQuiz' ? <ArticleIntroductionQuiz entitled={entitled} lessonId={introductionLesson?.id} items={introductionBundle.items} skillsByQuizId={introductionBundle.skillsByQuizId}
                                    onLesson={() => openIntroduction()} state={articleDrafts.introductionQuiz || {}} onStateChange={value => updateArticleDraft('introductionQuiz', value)} onReview={skill => openIntroduction(INTRO_SKILL_PARTS[skill])} />
                                : activePractice.kind === 'introductionSupplementQuiz' ? <ArticleIntroductionSupplementQuiz entitled={entitled} lessonId={supplementLesson?.id} items={supplementBundle.items} skillsByQuizId={supplementBundle.skillsByQuizId}
                                    onLesson={() => openSupplement(articleDrafts.supplementNavigation?.part)} state={articleDrafts.supplementQuiz || {}} onStateChange={value => updateArticleDraft('supplementQuiz', value)} onReview={skill => openSupplement('supp_' + skill + '_notice')} />
                                : activePractice.kind === 'quiz' ? (
                                    <PracticeQuiz items={pooledQuizItems} showLockedCta={showLockedCta} onComplete={(r) => recordQuizRun(POOLED_QUIZ_KEY, r)}
                                        persistEnabled={progressEnabled} savedQuizState={quizState?.[POOLED_QUIZ_KEY]} onSaveProgress={(blob) => saveQuizState(POOLED_QUIZ_KEY, blob)} />
                                ) : activePractice.kind === 'flashcards' ? (
                                    <PracticeFlashcards deck={flashcardItems} showCta={showLockedCta} title={title} />
                                ) : activePractice.kind === 'essay' ? (
                                    studyView ? <CanonicalArticleStudy entitled={entitled}
                                        sourceItem={paidItems.find(item => item.id === ARTICLE_MODEL_ID)} sourceScope="paidItems"
                                        annotationItem={paidItems.find(item => item.id === ARTICLE_PARAGRAPH_GUIDE_ID)} annotationScope="paidItems"
                                        mode={searchParams.get('view') === 'study-opening' ? 'opening' : 'full'}
                                        openingB={articlePilotStepContent({ entitled, guide: articleGuide, stepId: 'article_pilot_2' })?.openingB}
                                        onReturn={() => returnToArticle()} onNavigate={() => returnToArticle('article_pilot_7')} />
                                    : <PracticeEssayView key={ownerKey + '/' + activePractice.id} item={activePractice.item} packId={packId} entitled={entitled} tutorFeeNote={tutorFeeNote}
                                        minutesOverride={essaySession && essaySession.phase !== 'setup' ? essaySession.minutes : (independentView ? 45 : undefined)} pageSession={essaySession} onPageSessionChange={retainEssaySession} onPhaseChange={receiveEssayPhase} />
                                ) : null}
                                </div>
                            ) : (
                            <div className="analysis-content">
                            {!activeGroup || !selItem ? (
                                <p className="font-serif italic text-book-muted">Select a section.</p>
                            ) : (
                                introductionSelected ? <ArticleIntroductionLesson key={ownerKey + '/' + introductionLesson.id} lesson={introductionLesson} partId={introPart}
                                    onPart={openIntroduction} onReturn={() => returnToArticle('article_pilot_2')} onQuiz={openIntroductionQuiz} quizAvailable={!!introductionBundle.items.length}
                                    drafts={articleDrafts.introductionLesson || {}} onDraftChange={(id, value) => updateArticleDraft('introductionLesson', { [id]: value })}
                                    supplementAvailable={!!supplementLesson} onSupplement={() => openSupplement()} />
                                : supplementSelected ? <ArticleIntroductionSupplement key={ownerKey + '/' + supplementLesson.id} lesson={supplementLesson} partId={supplementPart}
                                    onPart={openSupplement} onReturn={() => returnToArticle('article_pilot_2')} onMain={introductionLesson ? () => openIntroduction() : undefined}
                                    onQuiz={openSupplementQuiz} quizAvailable={!!supplementBundle.items.length}
                                    drafts={articleDrafts.supplementLesson || {}} onDraftChange={(id, value) => updateArticleDraft('supplementLesson', { [id]: value })}
                                    inlineState={articleDrafts.supplementInline || {}} onInlineStateChange={value => updateArticleDraft('supplementInline', value)} />
                                    : config.renderSection(selItem, activeGroup, completedSet.has(selItem.id), { packId, availableAudios: visibleAudio, state: missionSessions[selItem.id] || {}, onChange: value => setMissionSessions(previous => mergeLessonSession(previous, selItem.id, value)), independent: independentWriting })
                            )}

                            {/* Per-section "Drill it" (teach -> see -> revise -> test -> hear): the
                                quizzes + flashcards linked to THIS section, between the content and
                                its audio. Shared component (settext-section-drills) -- the set-text
                                reader renders the same block from the same code. Renders nothing for
                                a section with no runnable linked quiz and no linked card. */}
                            {!introductionSelected && !supplementSelected && <SectionDrills section={selItem}
                                quizItems={quizItems} flashcardItems={flashcardItems} knownItemIds={knownItemIds}
                                showLockedCta={showLockedCta}
                                recordQuizRun={recordQuizRun} persistEnabled={progressEnabled}
                                quizState={quizState} saveQuizState={saveQuizState} />}

                            {/* Inline audio for the active section (free: public URL; paid: signed URL via CF). */}
                            {selItem && (
                                <div ref={textAudioRef}>
                                    <AudioList heading="Audio" audios={audioByItemId[selItem.id] || []} packId={packId} />
                                </div>
                            )}

                            {/* lesson-3b: close the loop -- the next lesson, or the fact
                                that this was the last one. */}
                            {selItem && (
                                <NextLessonCard next={nextLesson} blocksFor={blocksFor} onGo={goEntry} />
                            )}
                            </div>
                            )}
                            {/* Pack-level podcasts (no linked section) -- always available in the
                                content column so pack-level audio is never invisible. */}
                            {!independentWriting && <div ref={packAudioRef}>
                                <AudioList heading="Analysis Podcasts" audios={packAudio} packId={packId} />
                            </div>}
                        </div>
                    </div>
                )}
            </div>

            {/* AI Study Partner drawer -- same wiring as the set-text reader. */}
            <ReaderAiDrawer
                open={aiOpen && !independentWriting}
                onClose={() => setAiOpen(false)}
                packId={packId}
                packTitle={title}
                entitled={entitled}
                currentItemId={selItem ? selItem.id : null}
                currentItemType={config.itemType}
                currentItemTitle={activeItemTitle} />

            {/* My Notes drawer (user-owned, .selectable so content-protection won't block typing). */}
            <ReaderNotesDrawer
                open={notesOpen && !independentWriting}
                onClose={() => setNotesOpen(false)}
                packId={packId}
                itemId={selItem ? selItem.id : null}
                itemType={config.itemType}
                title={activeItemTitle} />

            {/* lesson-3a mobile lesson bar + sections sheet. The bar is hidden while a
                practice/essay view is active and while either drawer is open, so it can
                never cover the essay textarea, its Submit, or a drawer's controls. */}
            <SectionsSheet open={sheetOpen} onClose={closeNav}>
                {navContent}
            </SectionsSheet>
            {/* Floating Ask AI (reader-ai-fab). Same hide list as the lesson bar above --
                this reader's "no practice session" is !activePractice -- plus the open
                sheet and the header's own guard (openAi is what the shell renders on). */}
            {showAskAiFab({ canAsk: independentWriting ? null : openAi, initialLoading, aiOpen, notesOpen, inPractice: !!activePractice, sheetOpen }) && (
                <AskAiFab onClick={openAi} packId={packId} itemId={selItem?.id} />
            )}
            <LessonToast toast={toast} onDismiss={() => setToast(null)}
                onAction={() => { const n = toast && toast.next; setToast(null); if (n) goEntry(n); }} />
        </ReaderPageShell>
        </AnnotationsContext.Provider>
    );
}
